// Single source of truth for listing-platform metadata used across the
// listings surfaces (Tell Us About Your Listings flow, channel-detail, the
// listings table, marketing-channels service, add-reservation source dropdown).
// Adding a new platform should only require adding an entry here.

export type PlatformCategory = 'short_term' | 'long_term' | 'mid_term' | 'room_rental';

export type PlatformConnectFlow = 'cohost' | 'syndication';

// One step of a platform's connect/handover process, rendered as an icon stepper
// on the listing detail page while the listing is `connecting`.
export interface ConnectStep {
  icon: string;
  title: string;
  detail?: string;
}

export interface ListingPlatform {
  value: string;
  viewValue: string;
  category: PlatformCategory;
  idLabel: string;
  idHint?: string;
  connectFlow: PlatformConnectFlow;
  // Only Airbnb is a true co-host invite. Vrbo can't transfer listings between accounts, so we
  // rebuild the listing under ours, and Booking.com runs a formal change-of-ownership — so the
  // connect step's title, intro, steps and closing note are worded per platform rather than
  // blanket "co-host".
  connectTitle?: string;
  connectIntro?: string;
  connectSteps?: ConnectStep[];
  // Closing paragraph shown under the steps, for platforms where the handover
  // continues on our side after the client finishes their part.
  connectOutro?: string;
  // The "Copy Email" link only belongs on flows whose steps actually ask for pm@tidy.com.
  // The Vrbo/Booking.com transfers are handled by our team, not by an email invite.
  showConnectEmail?: boolean;
  // How a `connecting` backend row reads to the customer on the listings surfaces.
  connectingLabel?: string;
  // Connecting detail page: who the client sends their part to and what the account
  // manager does next. Absent for Airbnb, whose co-host invite completes on its own.
  contactPrompt?: string;
  // The c.tidy action item for a listing the client adds, when TIDY's team has a
  // handover to run. Without it the item reads "Make sure the <Platform> listing connects".
  connectItemTitle?: string;
  // The Tell Us About Your Listings summary: the one thing TIDY is waiting on the
  // client for, plus what happens once they've done it.
  waitingOnYou?: string;
  waitingOnYouDetail?: string;
  logo: string;
}

// The direct booking site rides on the same address-listing table as the real
// channels, under this platform value. It is deliberately absent from
// LISTING_PLATFORMS — that constant also drives the platform pickers, and
// TIDY isn't a channel a host can add from there — but it still reaches every
// place that renders a platform, so it needs a label and a logo of its own.
export const DIRECT_BOOKING_PLATFORM = 'tidy';

// The three short-term channels every address always gets a row for on the
// Listings page (and the checkboxes in Tell Us About Your Listings), in display order.
export const STR_CHANNEL_PLATFORMS = ['airbnb', 'vrbo', 'booking_com'] as const;

// The white/tightly-cropped brand marks drawn on a brand-coloured circle by the
// global `.platform-avatar` styles — the round platform badges used by the
// Connected Listings modal, the listing detail page, and the listings table.
// Platforms outside this map fall back to their full-colour LISTING_PLATFORMS logo.
export const PLATFORM_MARK_LOGOS: Record<string, string> = {
  airbnb: 'assets/img/airbnb-belo.svg',
  vrbo: 'assets/img/vrbo-icon.png',
  booking_com: 'assets/img/booking-com-icon.png',
  [DIRECT_BOOKING_PLATFORM]: 'assets/img/bowtie-white.svg',
};

export const PLATFORM_CATEGORY_LABELS: Record<PlatformCategory, string> = {
  short_term: 'Short-Term Sites',
  long_term: 'Long-Term Sites',
  mid_term: 'Mid-Term Sites',
  room_rental: 'By-the-Room Sites'
};

export const LISTING_PLATFORMS: ListingPlatform[] = [
  {
    value: 'airbnb',
    viewValue: 'Airbnb',
    category: 'short_term',
    idLabel: 'AirBnB Listing (ID or URL)',
    connectFlow: 'cohost',
    connectTitle: 'Add Us as a Co-Host',
    connectIntro: 'Add pm@tidy.com as a co-host to complete the connection.',
    showConnectEmail: true,
    connectingLabel: 'Waiting on you to add TIDY as a cohost',
    waitingOnYou: 'Add pm@tidy.com as a co-host on Airbnb',
    waitingOnYouDetail: 'We connect automatically as soon as the invitation is accepted.',
    logo: 'assets/img/listing-platforms/airbnb.png',
    connectSteps: [
      { icon: 'assets/svg/person-add-outline.svg', title: 'Invite a co-host', detail: 'Open your listing’s Co-Hosts page and click “Invite a co-host”.' },
      { icon: 'assets/svg/mail-outline.svg', title: 'Enter pm@tidy.com', detail: 'Use pm@tidy.com as the co-host email.' },
      { icon: 'assets/svg/key-outline.svg', title: 'Set permissions and send', detail: 'Choose the co-host permissions and click “Send Invitation”.' },
    ],
    connectOutro: 'We accept the invitation and your reservations start syncing automatically.'
  },
  {
    value: 'vrbo',
    viewValue: 'VRBO',
    category: 'short_term',
    idLabel: 'VRBO Listing ID',
    connectFlow: 'cohost',
    connectTitle: 'Transferring Your Vrbo Listing',
    connectIntro: 'Vrbo does not allow listings to be transferred between accounts, so we create a new listing under our account. We need four things from you:',
    connectingLabel: 'TIDY will work with you to create a new listing and transfer your reviews',
    contactPrompt: 'Send your booking list and written review consent to your account manager. They will reach out to set the handover date, and you can contact them any time for status.',
    connectItemTitle: 'Re-create the VRBO listing under TIDY\'s account',
    waitingOnYou: 'Send us your Vrbo booking list and review consent',
    waitingOnYouDetail: 'We create the new listing under our account and tell you when to block dates on your existing one.',
    logo: 'assets/img/listing-platforms/vrbo.png',
    connectSteps: [
      { icon: 'assets/svg/list-outline.svg', title: 'Send your booking list', detail: 'Guest names, dates, rates, and contact details for every reservation already on the books.' },
      { icon: 'assets/svg/document-text-outline.svg', title: 'Confirm your review transfer in writing', detail: 'Written consent that you want your reviews moved to the new listing.' },
      { icon: 'assets/svg/calendar-outline.svg', title: 'Block future dates on handover day', detail: 'Block them; never cancel them, because cancellations carry penalties on your account.' },
      { icon: 'assets/svg/eye-off-outline.svg', title: 'Unpublish your existing listing', detail: 'Once we let you know that the last guest has checked out.' },
    ],
    connectOutro: 'Your existing bookings stay on your listing and are paid out to you as normal. We handle those guests. Once the listing is inactive, we ask Vrbo to move your review history to the new listing. Vrbo usually approves the request, but the transfer is not guaranteed.'
  },
  {
    value: 'booking_com',
    viewValue: 'Booking.com',
    category: 'short_term',
    idLabel: 'Booking.com Listing ID',
    connectFlow: 'cohost',
    connectTitle: 'Transferring Your Booking.com Listing',
    connectIntro: 'Booking.com has a formal change-of-ownership process. We need two things from you:',
    connectingLabel: 'TIDY will work with you to transfer ownership',
    contactPrompt: 'Your account manager will contact you with the Partner Support message to paste. Reach out any time for status or questions.',
    connectItemTitle: 'Transfer the Booking.com listing to TIDY',
    waitingOnYou: 'Transfer ownership of your Booking.com listing to TIDY',
    waitingOnYouDetail: 'We write the Partner Support message for you to paste; you clear any open invoices.',
    logo: 'assets/img/listing-platforms/booking_com.png',
    connectSteps: [
      { icon: 'assets/svg/chatbox-ellipses-outline.svg', title: 'Send one message to Partner Support', detail: 'Through your Extranet inbox. We write it for you; you only paste and send. It confirms the handover date and our contact details.' },
      { icon: 'assets/svg/card-outline.svg', title: 'Clear outstanding invoices', detail: 'The transfer cannot be completed until the account balance is settled.' },
    ],
    connectOutro: 'Booking.com confirms the change with both of us. We then provide our financial details, and Booking.com issues a new contract. Be aware that Booking.com may stop displaying your existing reviews after ownership changes. Before you begin, we will ask Booking.com to confirm how your reviews will be treated.'
  },
  {
    value: 'zillow',
    viewValue: 'Zillow',
    category: 'long_term',
    idLabel: 'Zillow listing URL or ZPID',
    connectFlow: 'syndication',
    logo: 'assets/img/listing-platforms/zillow.png',
  },
  {
    value: 'apartments_com',
    viewValue: 'Apartments.com',
    category: 'long_term',
    idLabel: 'Listing URL',
    connectFlow: 'syndication',
    logo: 'assets/img/listing-platforms/apartments_com.png',
  },
  {
    value: 'zumper',
    viewValue: 'Zumper',
    category: 'long_term',
    idLabel: 'Listing URL',
    connectFlow: 'syndication',
    logo: 'assets/img/listing-platforms/zumper.png',
  },
  {
    value: 'furnished_finder',
    viewValue: 'Furnished Finder',
    category: 'mid_term',
    idLabel: 'Property ID or listing URL',
    connectFlow: 'syndication',
    logo: 'assets/img/listing-platforms/furnished_finder.png',
  },
  {
    value: 'padsplit',
    viewValue: 'PadSplit',
    category: 'room_rental',
    idLabel: 'PadSplit property URL or ID',
    connectFlow: 'syndication',
    logo: 'assets/img/listing-platforms/padsplit.png',
  }
];

export function platformItems(): { value: string; viewValue: string; logo: string }[] {
  return LISTING_PLATFORMS.map(({ value, viewValue, logo }) => ({ value, viewValue, logo }));
}

export function platformItemsByCategory(): { category: PlatformCategory; label: string; items: { value: string; viewValue: string; logo: string }[] }[] {
  return (Object.keys(PLATFORM_CATEGORY_LABELS) as PlatformCategory[])
    .map(category => ({
      category,
      label: PLATFORM_CATEGORY_LABELS[category],
      items: LISTING_PLATFORMS
        .filter(platform => platform.category === category)
        .map(({ value, viewValue, logo }) => ({ value, viewValue, logo }))
    }))
    .filter(group => group.items.length > 0);
}

export function getListingPlatform(value: string): ListingPlatform | undefined {
  return LISTING_PLATFORMS.find(platform => platform.value === value);
}

export function formatPlatformName(value: string): string {
  if (value === DIRECT_BOOKING_PLATFORM) {return 'TIDY';}
  return getListingPlatform(value)?.viewValue || value;
}

export function getPlatformIdLabel(value: string): string {
  return getListingPlatform(value)?.idLabel || 'Listing ID';
}

export function getConnectFlow(value: string): PlatformConnectFlow {
  return getListingPlatform(value)?.connectFlow || 'syndication';
}

export function getConnectTitle(value: string): string {
  return getListingPlatform(value)?.connectTitle || 'Connect Your Listing';
}

export function getConnectIntro(value: string): string {
  return getListingPlatform(value)?.connectIntro || 'Complete the connection to finish adding your listing.';
}

export function getConnectSteps(value: string): ConnectStep[] {
  return getListingPlatform(value)?.connectSteps || [];
}

export function getConnectOutro(value: string): string {
  return getListingPlatform(value)?.connectOutro || '';
}

export function showConnectEmail(value: string): boolean {
  return !!getListingPlatform(value)?.showConnectEmail;
}

// The customer-facing read of a `connecting` row: Booking.com and Vrbo are waiting on a
// handover our team runs, so they say so instead of a generic "Connecting".
export function getConnectingLabel(value: string): string {
  return getListingPlatform(value)?.connectingLabel || 'Connecting';
}

export function getContactPrompt(value: string): string {
  return getListingPlatform(value)?.contactPrompt || '';
}

export function getConnectItemTitle(value: string): string {
  return getListingPlatform(value)?.connectItemTitle || `Make sure the ${formatPlatformName(value)} listing connects`;
}

export function getWaitingOnYou(value: string): { title: string; detail: string } {
  const platform = getListingPlatform(value);
  return {
    title: platform?.waitingOnYou || `Finish connecting your ${formatPlatformName(value)} listing`,
    detail: platform?.waitingOnYouDetail || '',
  };
}

// Only the short-term channels (Airbnb / VRBO / Booking.com) are wired to a backend
// today. The long-term, mid-term and by-the-room platforms added for the rental
// roadmap are visible but gated behind the account manager (FeatureGateService).
export function isGatedPlatform(value: string): boolean {
  const category = getListingPlatform(value)?.category;
  return !!category && category !== 'short_term';
}
